import type { Product, ProductDatacenter } from '@/api/products';
import type { MonthlyPrice } from '@/api/types';
import { getCountryInfo, normalizeCountryCode } from '@/lib/country';
import { formatMonthlyNetPrice } from '@/lib/format';

export interface LocationOption {
  code: string;
  name: string;
  flag: string;
  available: boolean;
  monthlyPrice?: MonthlyPrice | null;
}

export function formatLocationOptionLabel(loc: LocationOption): string {
  const availability = loc.available ? '' : ' (Unavailable)';
  const price = formatMonthlyNetPrice(loc.monthlyPrice);
  const priceSuffix = price ? ` · ${price}` : '';
  return `${loc.flag} ${loc.name}${availability}${priceSuffix}`;
}

function datacenterCode(dc: { id?: string | null; name?: string | null } | null | undefined) {
  return String(dc?.id ?? dc?.name ?? '').trim();
}

function priceByDatacenterCode(product: Product): Map<string, MonthlyPrice> {
  const prices = new Map<string, MonthlyPrice>();
  const add = (dc: ProductDatacenter | null | undefined) => {
    const code = datacenterCode(dc);
    const price = dc?.monthlyPrice;
    if (!code || !price) return;
    // Prefer the first seen price; available datacenters are added first by callers.
    if (!prices.has(code)) prices.set(code, price);
  };
  for (const dc of product.datacenters ?? []) add(dc);
  for (const dc of product.supportedDatacenters ?? []) add(dc);
  return prices;
}

/** DigitalOcean has several regions per city, such as nyc1 and nyc3; their code tells them apart. */
function withDistinctNames(options: LocationOption[]): LocationOption[] {
  const counts = new Map<string, number>();
  for (const option of options) counts.set(option.name, (counts.get(option.name) ?? 0) + 1);
  return options.map((option) =>
    (counts.get(option.name) ?? 0) > 1
      ? { ...option, name: `${option.name} · ${option.code}` }
      : option
  );
}

export function locationOptions(product: Product | undefined): LocationOption[] {
  if (!product) return [];

  const supportedDatacenters = product.supportedDatacenters ?? [];
  const availableDatacenters = product.datacenters ?? [];
  const prices = priceByDatacenterCode(product);
  const supportedList =
    supportedDatacenters.length > 0 ? supportedDatacenters : availableDatacenters;

  if (supportedList.length > 0) {
    const uniq = new Set<string>();
    const result: LocationOption[] = [];
    const availableCodes = new Set<string>();
    for (const dc of availableDatacenters) {
      const code = datacenterCode(dc);
      if (code) availableCodes.add(code);
    }
    for (const dc of supportedList) {
      const code = datacenterCode(dc);
      if (!code || uniq.has(code)) continue;
      uniq.add(code);
      const countryCode = normalizeCountryCode(dc?.country ?? '');
      const info = getCountryInfo(countryCode);
      const city = String(dc?.city ?? '').trim();
      const label = city ? `${info.name} (${city})` : info.name;
      result.push({
        code,
        name: label,
        flag: info.flag,
        available: availableCodes.has(code),
        monthlyPrice: prices.get(code) ?? null,
      });
    }
    return withDistinctNames(result);
  }

  const cities = (product.cities ?? []) as (string | null | undefined)[];
  const countries = (product.countries ?? []) as (string | null | undefined)[];
  if (cities.length > 0 && countries.length === 1) {
    const countryCode = normalizeCountryCode(countries[0]);
    const info = getCountryInfo(countryCode);
    const uniq = new Set<string>();
    const result: LocationOption[] = [];
    for (const city of cities) {
      const cleanCity = String(city ?? '').trim();
      if (!cleanCity || uniq.has(cleanCity)) continue;
      uniq.add(cleanCity);
      result.push({
        code: cleanCity,
        name: `${info.name} (${cleanCity})`,
        flag: info.flag,
        available: true,
        monthlyPrice: prices.get(cleanCity) ?? null,
      });
    }
    return result;
  }

  const codes = (product.countries ?? []) as (string | null | undefined)[];
  if (!codes.length) return [] as LocationOption[];
  const uniq = new Set<string>();
  const result: LocationOption[] = [];
  for (const c of codes) {
    const code = normalizeCountryCode(c);
    if (!code || uniq.has(code)) continue;
    uniq.add(code);
    const info = getCountryInfo(code);
    result.push({
      code: info.code,
      name: info.name,
      flag: info.flag,
      available: true,
      monthlyPrice: prices.get(info.code) ?? null,
    });
  }
  return result;
}
